import {
  matchMutation,
  notifyManager,
  useQueryClient,
  type MutationCache,
  type MutationKey,
  type QueryClient,
} from "@tanstack/react-query";
import { useMemo, useSyncExternalStore } from "react";

function createPendingStore(cache: MutationCache, mutationKey: MutationKey) {
  const filters = { mutationKey, status: "pending" as const };
  let members = new Set(cache.getAll());
  const pending = new Set([...members].filter((mutation) => matchMutation(filters, mutation)));
  const getSnapshot = () => pending.size > 0;

  return {
    getSnapshot,
    subscribe(onChange: () => void) {
      const notify = notifyManager.batchCalls(onChange);
      // React may subscribe after a mutation changed, or re-subscribe in Strict Mode.
      members = new Set(cache.getAll());
      pending.clear();
      for (const mutation of members) {
        if (matchMutation(filters, mutation)) {
          pending.add(mutation);
        }
      }
      const unsubscribe = cache.subscribe((event) => {
        const mutation = event.mutation;
        if (mutation === undefined) {
          return;
        }
        const before = getSnapshot();
        if (event.type === "removed") {
          members.delete(mutation);
          pending.delete(mutation);
        } else {
          if (event.type === "added") {
            members.add(mutation);
          }
          // A cleared/removed in-flight mutation can still emit later updates.
          if (!members.has(mutation)) {
            return;
          }
          if (matchMutation(filters, mutation)) {
            pending.add(mutation);
          } else {
            pending.delete(mutation);
          }
        }
        if (getSnapshot() !== before) {
          notify();
        }
      });
      onChange();
      return unsubscribe;
    },
  };
}

/**
 * Observe pending mutations under a stable key prefix without rescanning the cache on
 * every mutation-observer options update. Prepared Learn surfaces own many observers;
 * upstream useIsMutating materializes/filter-scans the whole cache for each such event.
 * Only the changed mutation needs matching, and consumers only need the busy boolean.
 */
export function useIsMutationPending(mutationKey: MutationKey, queryClient?: QueryClient) {
  const cache = useQueryClient(queryClient).getMutationCache();
  const store = useMemo(() => createPendingStore(cache, mutationKey), [cache, mutationKey]);
  return useSyncExternalStore(store.subscribe, store.getSnapshot, store.getSnapshot);
}
